import { useEffect, useMemo, useState } from 'react';

export const USER_PROFILE_LIST_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

export function readStoredProfileListPageSize(storageKey, fallback = 10) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = sessionStorage.getItem(storageKey);
    const n = parseInt(raw, 10);
    if (USER_PROFILE_LIST_PAGE_SIZE_OPTIONS.includes(n)) return n;
  } catch {
    /* ignore */
  }
  return fallback;
}

/**
 * Shared pagination state for profile list panels (Snippets-style footer).
 * @param {number} itemCount
 * @param {{ storageKey: string, resetDeps?: unknown[] }} options
 */
export default function useProfileListPagination(itemCount, { storageKey, resetDeps = [] }) {
  const [pageSize, setPageSize] = useState(() => readStoredProfileListPageSize(storageKey));
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when caller deps change
  }, resetDeps);

  const { totalPages, rangeStart, rangeEnd } = useMemo(() => {
    const total = itemCount;
    const tp = Math.max(1, Math.ceil(total / pageSize));
    const start = (currentPage - 1) * pageSize;
    const rs = total === 0 ? 0 : start + 1;
    const re = Math.min(start + pageSize, total);
    return { totalPages: tp, rangeStart: rs, rangeEnd: re };
  }, [itemCount, currentPage, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(itemCount / pageSize));
    setCurrentPage((p) => Math.min(Math.max(1, p), maxPage));
  }, [itemCount, pageSize]);

  const paginate = useMemo(
    () => (items) => {
      const start = (currentPage - 1) * pageSize;
      return items.slice(start, start + pageSize);
    },
    [currentPage, pageSize]
  );

  const handlePageSizeChange = (event) => {
    const n = parseInt(event.target.value, 10);
    if (!USER_PROFILE_LIST_PAGE_SIZE_OPTIONS.includes(n)) return;
    setPageSize(n);
    setCurrentPage(1);
    try {
      sessionStorage.setItem(storageKey, String(n));
    } catch {
      /* ignore */
    }
  };

  return {
    pageSize,
    currentPage,
    setCurrentPage,
    totalPages,
    rangeStart,
    rangeEnd,
    paginate,
    handlePageSizeChange
  };
}
